import ServiceCollection from 'src/services/ServiceCollection';
import {
    Button,
    Container,
    ExpandableSection,
    FormField,
    Grid,
    Header,
    SegmentedControl,
    Select,
    SpaceBetween,
    Tabs,
} from '@amzn/awsui-components-react/polaris';
import TPEAction from 'src/models/common/TPEAction';
import React, { useEffect, useState } from 'react';
import useReducerWithLogger from 'src/services/utils/ReducerWithLogger';
import { reportsReducer } from 'src/services/reports/ReportsReducer';
import { initialState, ReportsState } from 'src/services/reports/ReportsState';
import { CLIReportStatus, CLIWithStatus, GetCLIsByProviderCompanyCodeRequest, GetCLIReportStatusesForPeriodRequest, TPTaxReportMapResponse } from 'src/models/reports/TPTaxReport';
import CONSTANTS, { isPliTabKey, IS_TESTED_PARTY_REPORT_ENABLED } from 'src/utils/constants';
import { accountingDaysList } from 'src/models/reports/GetAccountingDayList';
import { SelectProps, Tiles } from '@amzn/awsui-components-react';
import TPTaxReportTableGrid from 'src/components/reports/TPTaxReportTableGrid';
import { PassthroughCostDetailSection, PLIReconciliationSection } from 'src/components/reports/PassthroughCostSections';
import { buildCliCurrencyMap, mapBackendFieldsToUIModel } from 'src/components/reports/TPMultiCLIReportsShared';
import { FetchReportRequest } from 'src/models/reports/FetchReport';
import { DownloadReportRequest } from 'src/models/reports/DownloadReport';
import { parseTpTaxUIFilter } from './../../utils/ReportUtils';
import { GenerateReportRequest, ReportRequestParams } from 'src/models/reports/GenerateReport';
import { useHistory, useParams } from 'react-router-dom';
import TPEErrorWatcher from '../shared/TPEErrorWatcher';
import { MANAGE_EXCEPTIONS_TAB_URL_PREFIX } from 'src/components/ic-account-mapping/constants';
import useLocalStorage from 'src/utils/useLocalStorage';
import { CompanyCodeType, getMappingsByTestedPartyCode, getProviderCodeForCli } from 'src/utils/TestedPartyUtils';
import { useTestedPartyMappings } from 'src/hooks/useTestedPartyMappings';
import { getFeatureFlags } from 'src/services/FeatureFlagsService';
import { useDashboardReportContext } from 'src/components/reports/useDashboardReportContext';
import 'src/components/reports/TPTaxReportView.scss';
import { AIInsightButton, AI_INSIGHT_REPORT_TYPES, buildTpTaxDefaultPrompt } from "src/components/shared/ai-insights";

export type ContextType = {
    state: ReportsState;
    dispatch: React.Dispatch<TPEAction>;
    services: ServiceCollection;
    // Calculation currency code (e.g. "EUR") for the selected CLI, shared with deep children (report
    // sections) via context rather than prop chains.
    currency: string;
};

const TPTaxReportProvider = (props: {
    state: ReportsState;
    dispatch: React.Dispatch<TPEAction>;
    services: ServiceCollection;
    currency: string;
    children: JSX.Element;
}) => {
    const { state, dispatch, services, currency, children } = props;
    const providerValue = React.useMemo(
        () => ({
            state,
            dispatch,
            services,
            currency,
        }),
        [state, dispatch, currency]
    );
    return <TPTaxReportContext.Provider value={providerValue}>{children}</TPTaxReportContext.Provider>;
};

export const TPTaxReportContext = React.createContext(null as ContextType | null);

/**
 * Wraps a report section and overlays the calculation currency (read from TPTaxReportContext) centered
 * on the section's header band. Cloudscape's ExpandableSection header slots (headerText/headerActions)
 * are edge-anchored, so an element placed there shifts with the adjacent button/badge and never lines up
 * across sections. The overlay keeps every currency at the same centered x-position (all section
 * containers are the same width). pointerEvents:none lets header clicks reach the expand/collapse toggle.
 * Currency is consumed from context here so no section needs it threaded as a prop.
 */
export const SectionWithCurrency = ({ children }: { children: React.ReactNode }): JSX.Element => {
    const currency = React.useContext(TPTaxReportContext)?.currency ?? '';
    return (
        <div className="tp-tax-report__section-wrapper">
            {currency ? <div className="tp-tax-report__currency-overlay">{currency}</div> : null}
            {children}
        </div>
    );
};

// Sentinel value for the Functional Segment dropdown's default "no filter" option.
export const FUNCTIONAL_SEGMENT_ALL = 'All';

// Prefix namespacing a CLI's functional segment on the option's hidden `filteringTags`, so it can't
// collide with the report-status value (also kept in filteringTags).
export const FSC_FILTER_TAG_PREFIX = 'fsc:';

// Badge color keys reportStatusBadge maps each MAR report status to; the palette lives in
// TPTaxReportView.scss as `.cli-status-badge--<color>`.
type CliBadgeColor = 'green' | 'grey' | 'red';

// Build the CLI dropdown options (CLI number + hidden `fsc:` functional-segment tag) for both modes.
// No badge is rendered here (report status is shown by buildCliOptionsWithReportStatus). When
// `enriched` is false (tpTaxCliEnrichmentEnabled off) fall back to plain CLI-number options.
export function buildCliOptions(cliDetails: CLIWithStatus[], enriched = true): SelectProps.Option[] {
    if (!enriched) {
        return cliDetails.map((cli) => ({
            label: cli.calculationNumber,
            value: cli.calculationNumber,
        }));
    }
    // Order by CLI number so the list is predictable and scannable.
    const byCliNumber = [...cliDetails].sort((a, b) =>
        (a.calculationNumber ?? '').localeCompare(b.calculationNumber ?? '', undefined, {
            numeric: true,
            sensitivity: 'base',
        }));
    return byCliNumber.map((cli) => ({
        label: cli.calculationNumber,
        value: cli.calculationNumber,
        // Functional-segment tag keeps the Functional Segment filter and search working.
        filteringTags: cli.functionalSegmentCategory
            ? [`${FSC_FILTER_TAG_PREFIX}${cli.functionalSegmentCategory}`]
            : [],
    }));
}

// Map a MAR report status (SUCCESS / FAILED / PENDING) to its badge color + pill text.
export function reportStatusBadge(status?: string): { color: CliBadgeColor; label: string } | null {
    if (!status) {
        return null;
    }
    switch (status.toUpperCase()) {
        case 'SUCCESS':
            return { color: 'green', label: 'Success' };
        case 'FAILED':
            return { color: 'red', label: 'Failed' };
        case 'PENDING':
            return { color: 'grey', label: 'Pending' };
        default:
            return null;
    }
}

// Render the CLI number with a right-aligned report-status badge in the option's labelContent slot.
export function cliLabelWithReportStatus(calculationNumber: string, status?: string): React.ReactNode {
    const badge = reportStatusBadge(status);
    return (
        <span className="cli-status-label">
            <span>{calculationNumber}</span>
            {badge ? <span className={`cli-status-badge cli-status-badge--${badge.color}`}>{badge.label}</span> : null}
        </span>
    );
}

// Sort group for report status: succeeded first, then failed, then pending, then unrecognized.
export function reportStatusSortGroup(status?: string): number {
    switch ((status ?? '').toUpperCase()) {
        case 'SUCCESS':
            return 0;
        case 'FAILED':
            return 1;
        case 'PENDING':
            return 2;
        default:
            return 3;
    }
}

// Build the CLI dropdown options FILTERED to CLIs that have a MAR report for the period/day (those
// present in cliReportStatuses); CLIs with no report are omitted. Each shows a report-status badge and
// keeps its report status + `fsc:` functional-segment filteringTags, grouped by outcome then CLI number.
// Callers use this only when the feature is on and a status list was returned; else buildCliOptions.
export function buildCliOptionsWithReportStatus(
    cliDetails: CLIWithStatus[],
    cliReportStatuses: CLIReportStatus[],
    enriched = true
): SelectProps.Option[] {
    const statusByCli = new Map(cliReportStatuses.map((r) => [r.calculationNumber, r.status]));
    const present = cliDetails.filter((cli) => statusByCli.has(cli.calculationNumber));
    const ordered = [...present].sort((a, b) => {
        const groupDelta =
            reportStatusSortGroup(statusByCli.get(a.calculationNumber)) -
            reportStatusSortGroup(statusByCli.get(b.calculationNumber));
        if (groupDelta !== 0) {
            return groupDelta;
        }
        return (a.calculationNumber ?? '').localeCompare(b.calculationNumber ?? '', undefined, {
            numeric: true,
            sensitivity: 'base',
        });
    });
    return ordered.map((cli) => {
        const reportStatus = statusByCli.get(cli.calculationNumber);
        return {
            label: cli.calculationNumber,
            value: cli.calculationNumber,
            // Non-enriched mode: bare CLI-number options, mirroring buildCliOptions(cliDetails, false).
            ...(enriched
                ? {
                      labelContent: cliLabelWithReportStatus(cli.calculationNumber, reportStatus),
                      filteringTags: [
                          ...(reportStatus ? [reportStatus] : []),
                          ...(cli.functionalSegmentCategory
                              ? [`${FSC_FILTER_TAG_PREFIX}${cli.functionalSegmentCategory}`]
                              : []),
                      ],
                  }
                : {}),
        };
    });
}

// Derive the Functional Segment dropdown options from the distinct, non-empty functional segment
// categories across the given CLIs, prefixed with "All" (the no-filter default). Shared by both modes.
export function buildFunctionalSegmentOptions(cliDetails: CLIWithStatus[]): SelectProps.Option[] {
    const distinctSegments = Array.from(
        new Set(
            cliDetails
                .map((cli) => cli.functionalSegmentCategory)
                .filter((segment): segment is string => !!segment && segment.trim() !== '')
        )
    ).sort((a, b) => a.localeCompare(b));
    return [
        { label: FUNCTIONAL_SEGMENT_ALL, value: FUNCTIONAL_SEGMENT_ALL },
        ...distinctSegments.map((segment) => ({ label: segment, value: segment })),
    ];
}

export default function TPTaxReportView(props: { services: ServiceCollection }) {
    const { services } = props;
    const history = useHistory();
    const { filter } = useParams<{ filter: string }>();
    const [currentFilter, setCurrentFilter] = useState(parseTpTaxUIFilter(decodeURIComponent(filter)) || '');
    const [state, dispatch] = useReducerWithLogger(reportsReducer, initialState);
    const [companyCodeType, setCompanyCodeType] = useState<CompanyCodeType>(CompanyCodeType.PROVIDER);
    const [companyCodeOption, setCompanyCodeOption] = React.useState({
        value: currentFilter.entity,
        label: currentFilter.entity,
    } as SelectProps.Option | null);
    const [companyCodeBySegmentList, setCompanyCodeBySegmentList] = React.useState([] as SelectProps.Option[]);
    const [selectedCLIOption, setSelectedCLIOption] = React.useState({
        value: currentFilter.cli,
        label: currentFilter.cli,
    } as SelectProps.Option | null);
    const [selectedAccountingDayOption, setSelectedAccountingDayOption] = React.useState({
        value: currentFilter.accountingDay,
        label: currentFilter.accountingDay,
    } as SelectProps.Option | null);
    const [selectedPeriodNameOption, setSelectedPeriodNameOption] = React.useState({
        value: currentFilter.periodName,
        label: currentFilter.periodName,
    } as SelectProps.Option | null);
    const [cliRequestPayload, setCliRequestPayload] = useState(null as GetCLIsByProviderCompanyCodeRequest | null);
    const [cliList, setCliList] = React.useState([] as SelectProps.Option[]);
    // Mode-agnostic raw CLI details backing the dropdown, set by both Provider and Tested Party modes.
    // A single build effect turns it into cliList + functional-segment options.
    const [dropdownCliDetails, setDropdownCliDetails] = React.useState([] as CLIWithStatus[]);
    // Functional Segment filter: after a company code is chosen, narrow the CLI dropdown to one segment.
    // Options are derived client-side from each CLI's functionalSegmentCategory. "All" applies no filter.
    const [functionalSegmentOptions, setFunctionalSegmentOptions] = React.useState([] as SelectProps.Option[]);
    const [selectedFunctionalSegmentOption, setSelectedFunctionalSegmentOption] = React.useState({
        label: FUNCTIONAL_SEGMENT_ALL,
        value: FUNCTIONAL_SEGMENT_ALL,
    } as SelectProps.Option);
    const [dropDownDisabledFlag, setDropDownDisabledFlag] = React.useState(true);
    const [fetchReportRequest, setFetchReportRequest] = React.useState(null as FetchReportRequest | null);
    const [reportData, setReportData] = React.useState(null as TPTaxReportMapResponse | null);
    const [clisResponse, getCLIsLoading, getCLIsError] =
        services.reportsService.getCLIsByProviderCompanyCode(cliRequestPayload);
    const [previousReportResponse, getPreviousReportLoading, getPreviousReportError] =
        services.reportsService.fetchReportData(fetchReportRequest);
    // Event-driven redesign: period + accounting day come from the dashboard (URL query); the on-page
    // period/day dropdowns are hidden and only the Company Code + CLI selectors remain.
    const dashboardContext = useDashboardReportContext(services.messageService);
    const dashboardDriven = dashboardContext.eventDrivenTpTaxReportingEnabled;
    // "Legacy mode" means the flag has RESOLVED and is off — not merely the default-false pre-resolution
    // value. Legacy-only URL rewrites (which drop the dashboard's ?period&accountingDay query) must key on
    // this: on a fresh dashboard-mode deep link the flag is briefly unresolved (dashboardDriven === false),
    // and rewriting the URL in that window would strip the query and trip the redirect-to-dashboard guard.
    const legacyMode = dashboardContext.flagResolved && !dashboardDriven;
    // Skip the reporting-period fetch when the period dropdown won't be shown. Also skip until the flag
    // resolves, so the call doesn't fire during the pre-resolution window (dashboardDriven is false then).
    const [accountingPeriodResult, accountingPeriodLoading, accountingPeriodError] =
        services.reportsService.getReportingPeriods(dashboardDriven);
    const [companyCodesBySegmentListResponse, isCompanyCodesBySegmentDataLoading, companyCodesBySegmentFetchError] =
        services.reportsService.getCOADataBySegment(CONSTANTS.COA_SEGMENT_MAPPING.COMPANY.WEB_API);
    const [reportCategory, setReportCategory] = React.useState(CONSTANTS.TP_TAX_REPORT_CATEGORY.MARGIN);
    const [cliDetails, cliDetailsLoading, cliDetailsError] = services.cliService.getCLIDetails(
        selectedCLIOption?.value
    );
    const [downloadReportRequestPayload, setDownloadReportRequestPayload] = React.useState(
        null as unknown as DownloadReportRequest
    );
    const [downloadReportUrl, downloadReportLoading, downloadReportError] =
        services.reportsService.downloadReport(downloadReportRequestPayload);
    const [generateReportRequest, setGenerateReportRequest] = React.useState(null as unknown as GenerateReportRequest);
    const [generateReportResponse, generateReportLoading, generateReportError] =
        props.services.reportsService.generateReport(generateReportRequest);
    const [isPassthroughDrilldownEnabled, setIsPassthroughDrilldownEnabled] = useState(false);
    const [isReportCurrencyOverlayEnabled, setIsReportCurrencyOverlayEnabled] = useState(false);
    // Gates the TP Tax (MAR) CLI dropdown enhancements: the Functional Segment filter and the
    // report-status feature (dropdown shows only CLIs with a report for the period/day, each badged).
    // When off, falls back to plain CLI-number options with the segment filter hidden.
    const [isTpTaxCliEnrichmentEnabled, setIsTpTaxCliEnrichmentEnabled] = useState(false);

    useEffect(() => {
        getFeatureFlags()
            .then((flags) => {
                setIsPassthroughDrilldownEnabled(flags.isPassthroughDrilldownEnabled);
                setIsReportCurrencyOverlayEnabled(flags.reportCurrencyOverlayEnabled);
                setIsTpTaxCliEnrichmentEnabled(flags.tpTaxCliEnrichmentEnabled);
            })
            .catch((err) => {
                console.error('Failed to load feature flags', err);
            });
    }, []);

    // Seed period/day from the dashboard context so the fetch (gated on all four being set) works once
    // company + CLI are chosen. (dashboardContext / dashboardDriven are declared above.)
    useEffect(() => {
        if (dashboardContext.periodOption && dashboardContext.accountingDayOption) {
            setSelectedPeriodNameOption(dashboardContext.periodOption);
            setSelectedAccountingDayOption(dashboardContext.accountingDayOption);
        }
    }, [dashboardContext.periodOption, dashboardContext.accountingDayOption]);

    // Period-scoped MAR report-status set, keyed on period + accounting day so it's fetched once per
    // period/day and reused across company-code changes and mode toggles, independently of CLI loading.
    // Null request => skipped (feature off or period/day unknown).
    const reportStatusRequest = React.useMemo<GetCLIReportStatusesForPeriodRequest | null>(() => {
        const period = selectedPeriodNameOption?.value;
        const accountingDay = selectedAccountingDayOption?.value;
        return isTpTaxCliEnrichmentEnabled && period && accountingDay
            ? { period, accountingDay }
            : null;
    }, [isTpTaxCliEnrichmentEnabled, selectedPeriodNameOption?.value, selectedAccountingDayOption?.value]);
    const [reportStatusResponse, reportStatusLoading, reportStatusError] =
        services.reportsService.getCLIReportStatusesForPeriod(reportStatusRequest);
    const cliReportStatuses = reportStatusResponse?.cliReportStatusList;

    // Tested party API integration - fetch mappings when toggle is set to Tested Party
    const [testedPartyMappings, testedPartyMappingsLoading, testedPartyError] = useTestedPartyMappings(
        companyCodeType === CompanyCodeType.TESTED_PARTY
    );

    // Reset all filter state when toggle changes (skip initial mount to preserve URL params)
    const isInitialMount = React.useRef(true);
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        setCompanyCodeOption(null);
        resetCliSelectionState();
        // Keep the dashboard-inherited period + day across a company-code-type toggle (they come from the
        // URL, not the on-page dropdowns which are hidden in that mode).
        if (legacyMode) {
            setSelectedPeriodNameOption(null);
            setSelectedAccountingDayOption(null);
        }
        // Legacy mode resets the URL to the bare report path. In dashboard mode period + day live in the
        // query string; pushing the bare path would drop them and trip the redirect-to-dashboard guard, so
        // leave the URL untouched (also skipped while the flag is unresolved — see legacyMode).
        if (legacyMode) {
            history.push('/reports/tpTaxReport');
        }
    }, [companyCodeType]);

    // Show error banner when tested party API fails
    useEffect(() => {
        if (testedPartyError) {
            services.messageService.showErrorAutoDismissBanner(testedPartyError);
        }
    }, [testedPartyError]);

    // Handle changes in the filter value
    const handleFilterChange = (newFilter: string) => {
        // Check if the filter string contains "++"
        if (newFilter.includes('+++')) {
            // Handle "++" as a special case
            // Set default or placeholder values for the dropdowns
            setCompanyCodeOption(null);
            setSelectedCLIOption(null);
            setSelectedPeriodNameOption(null);
            setSelectedAccountingDayOption(null);
        } else {
            setCurrentFilter(parseTpTaxUIFilter(newFilter) || '');
            const encodedFilter = encodeURIComponent(newFilter);
            if (legacyMode) {
                // Legacy: the filter lives entirely in the URL path (no query string).
                history.push(`/reports/tpTaxReport/${encodedFilter}`);
            } else if (dashboardDriven && newFilter) {
                // Dashboard mode: reflect the newly-viewed report in the URL path (restoring the old
                // deep-linkable behavior) while PRESERVING the ?period&accountingDay query — dropping the
                // query would trip the redirect-to-dashboard guard. Skip when newFilter is empty (a reset /
                // company-code change) so we don't push an empty path segment (/reports/tpTaxReport/?period=…).
                history.push(`/reports/tpTaxReport/${encodedFilter}${history.location.search}`);
            }
            // Flag not yet resolved: leave the URL untouched (don't act on the default-false flag); this
            // handler re-fires once the flag resolves and the dashboard branch above runs.
        }
    };

    useEffect(() => {
        if (companyCodeOption && selectedCLIOption && selectedPeriodNameOption && selectedAccountingDayOption) {
            let companyCode = companyCodeOption?.value || '';
            let cliValue = selectedCLIOption?.value || '';
            let periodName = selectedPeriodNameOption?.value || '';
            let accountingDay = selectedAccountingDayOption?.value || '';
            // Replace for url pattern
            cliValue = cliValue.replace('.', '*');
            const newFilterString = `${companyCode}+${cliValue}+${periodName}+${accountingDay}`;
            handleFilterChange(newFilterString);
        }
    }, [companyCodeOption, selectedCLIOption, selectedPeriodNameOption, selectedAccountingDayOption]);

    useEffect(() => {
        if (!clisResponse) {
            return;
        }
        // Provider mode: prefer CLIDetailsList (carries functionalSegmentCategory for the segment
        // filter); fall back to CLIList mapped to bare CLIWithStatus when the response predates enrichment.
        setDropdownCliDetails(
            clisResponse.CLIDetailsList && clisResponse.CLIDetailsList.length > 0
                ? clisResponse.CLIDetailsList
                : clisResponse.CLIList.map((value) => ({ calculationNumber: value })));
    }, [clisResponse]);

    // Single writer of cliList + functional-segment options for both modes. Rebuilds when the CLI details
    // or the period report-status set change, so the dropdown renders immediately and re-filters once
    // statuses arrive. When the feature is on and statuses are available, shows only CLIs with a report.
    useEffect(() => {
        if (isTpTaxCliEnrichmentEnabled && reportStatusError) {
            // Status fetch failed: keep the list empty (and the Select disabled below) rather than fall
            // back to the full unfiltered list, which would let the user pick a CLI with no report.
            setCliList([]);
        } else if (isTpTaxCliEnrichmentEnabled && cliReportStatuses) {
            setCliList(buildCliOptionsWithReportStatus(dropdownCliDetails, cliReportStatuses, isTpTaxCliEnrichmentEnabled));
        } else {
            setCliList(buildCliOptions(dropdownCliDetails, isTpTaxCliEnrichmentEnabled));
        }
        setFunctionalSegmentOptions(
            dropdownCliDetails.length > 0 ? buildFunctionalSegmentOptions(dropdownCliDetails) : []);
    }, [dropdownCliDetails, cliReportStatuses, isTpTaxCliEnrichmentEnabled, reportStatusError]);

    // The CLI dropdown shows either the full CLI list (when the Functional Segment filter is "All") or
    // only the CLIs whose functional segment matches the selected segment.
    const filteredCliList = React.useMemo(() => {
        const selectedSegment = selectedFunctionalSegmentOption?.value;
        if (!selectedSegment || selectedSegment === FUNCTIONAL_SEGMENT_ALL) {
            return cliList;
        }
        return cliList.filter((option) => option.filteringTags?.includes(`${FSC_FILTER_TAG_PREFIX}${selectedSegment}`));
    }, [cliList, selectedFunctionalSegmentOption]);

    // When the functional segment changes, clear a CLI selection no longer in the filtered list.
    useEffect(() => {
        // Wait for CLIs to load first, so a URL-restored selection isn't wiped on mount. Guard on the
        // base cliList (not filteredCliList) so a segment filter excluding the current CLI still clears it.
        if (cliList.length === 0) {
            return;
        }
        if (
            selectedCLIOption &&
            !filteredCliList.some((option) => option.value === selectedCLIOption.value)
        ) {
            setSelectedCLIOption(null);
        }
    }, [filteredCliList]);

    useEffect(() => {
        if (!companyCodesBySegmentListResponse) {
            return;
        }
        setCompanyCodeBySegmentList([
            ...companyCodesBySegmentListResponse.map((value) => ({
                label: `${value.codeId} - ${value.codeName}`,
                value: value.codeId,
            })),
        ]);
    }, [companyCodesBySegmentListResponse]);

    const onViewReportClicked = () => {
        if (
            isOptionDefined(companyCodeOption) &&
            isOptionDefined(selectedPeriodNameOption) &&
            isOptionDefined(selectedCLIOption) &&
            isOptionDefined(selectedAccountingDayOption)
        ) {
            setFetchReportRequest({
                reportName: CONSTANTS.REPORT_TYPES.TP_TAX,
                params: fetchReportRequestParams(),
            });
        }
    };

    const onGenerateReportButtonClicked = () => {
        if (
            isOptionDefined(companyCodeOption) &&
            isOptionDefined(selectedPeriodNameOption) &&
            isOptionDefined(selectedCLIOption) &&
            isOptionDefined(selectedAccountingDayOption)
        ) {
            setGenerateReportRequest({
                userId: useLocalStorage.getUsernameFromLocalStorage(),
                reportName: CONSTANTS.REPORT_TYPES.TP_TAX,
                params: fetchReportRequestParams(),
            });
        }
    };

    useEffect(() => {
        if (!previousReportResponse) {
            return;
        }
        if (previousReportResponse.statusMessage === null) {
            const parsed = JSON.parse(previousReportResponse.payload);
            mapBackendFieldsToUIModel(parsed);
            setReportData(parsed);
        } else {
            setReportData(null);
            services.messageService.showErrorAutoDismissBanner(previousReportResponse.statusMessage);
        }
    }, [previousReportResponse]);

    useEffect(() => {
        if (!generateReportResponse) {
            return;
        }
        if (generateReportResponse.reportId !== null) {
            services.messageService.showSuccessAutoDismissBanner(
                'Report generation initiated successfully! You can view report after couple of minutes'
            );
        } else {
            services.messageService.showErrorAutoDismissBanner(
                'Failed to initiate report generation. Contact Engineering Team'
            );
        }
    }, [generateReportResponse]);

    useEffect(() => {
        if (downloadReportError) {
            services.messageService.showErrorBanner(downloadReportError);
        } else if (downloadReportUrl) {
            const link = document.createElement('a');
            link.href = downloadReportUrl;
            link.setAttribute('download', '');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }, [downloadReportUrl, downloadReportError]);

    useEffect(() => {
        if (isOptionDefined(companyCodeOption)) {
            getCLIsBasedOnCompanyCodeSelected();
        }
    }, [companyCodeOption, testedPartyMappings, companyCodeType, testedPartyError, isTpTaxCliEnrichmentEnabled]);

    const getCLIsBasedOnCompanyCodeSelected = () => {
        if (isOptionDefined(companyCodeOption)) {
            if (companyCodeType === CompanyCodeType.PROVIDER) {
                setCliRequestPayload({
                    providerCompanyCode: companyCodeOption?.value as string,
                    calculationStatus: CONSTANTS.CALCULATION_STATUS.ACTIVE,
                });
            } else {
                // Tested Party mode - filter CLIs (with functional segment) from the cached mappings.
                // Mirrors the Provider path so the CLI dropdown can be narrowed by functional segment,
                // all client-side from the single config download.
                if (!testedPartyError) {
                    const tpMappings = getMappingsByTestedPartyCode(
                        testedPartyMappings,
                        companyCodeOption?.value as string
                    );
                    // Adapt mappings to the CLIWithStatus shape the shared option builders expect.
                    const cliDetails: CLIWithStatus[] = tpMappings.map((m) => ({
                        calculationNumber: m.cliNumber,
                        functionalSegmentCategory: m.functionalSegmentCategory,
                    }));
                    // Feed the mode-agnostic details; the single build effect renders the dropdown +
                    // functional-segment options and applies success ordering when available.
                    setDropdownCliDetails(cliDetails);
                }
            }
            setDropDownDisabledFlag(false);
        }
    };

    // Clear the CLI dropdown, its functional-segment filter, and the loaded report. Shared by the
    // company-code-type toggle, Reset Filters, and the company-code change. Site-specific resets
    // (company-code option, period/day, URL) stay at the call sites since they differ per trigger.
    const resetCliSelectionState = () => {
        setDropDownDisabledFlag(true);
        setCliList([]);
        setDropdownCliDetails([]);
        setFunctionalSegmentOptions([]);
        setSelectedFunctionalSegmentOption({ label: FUNCTIONAL_SEGMENT_ALL, value: FUNCTIONAL_SEGMENT_ALL });
        setReportData(null);
        setSelectedCLIOption(null);
    };

    const onResetButtonClicked = () => {
        handleFilterChange('');
        setCompanyCodeOption(null);
        resetCliSelectionState();
        // In dashboard-driven mode period + accounting day are inherited from the URL, not user-selected,
        // so they must survive a reset; only the on-page selections (company code / CLI) are cleared.
        if (!dashboardDriven) {
            setSelectedAccountingDayOption(null);
            setSelectedPeriodNameOption(null);
        }
    };
    const resetOnCompanyCodeChange = () => {
        handleFilterChange('');
        resetCliSelectionState();
        // Preserve period + accounting day across a company-code change in both modes: dashboard mode
        // inherits them from the URL, and legacy mode keeps them so the period-scoped report-status set
        // (keyed on period+accountingDay) stays valid for the newly-selected company.
    };

    function isOptionDefined(option?: SelectProps.Option | null) {
        if (option == null) {
            return false;
        }
        return option && option.value !== undefined && option.label !== undefined && option.value !== '';
    }
    // Resolve providerCompanyCode based on toggle mode for report API
    function fetchReportRequestParams(): ReportRequestParams {
        let providerCompanyCode: string;
        if (companyCodeType === CompanyCodeType.TESTED_PARTY) {
            providerCompanyCode = getProviderCodeForCli(testedPartyMappings, selectedCLIOption?.value as string) || '';
        } else {
            providerCompanyCode = companyCodeOption?.value as string;
        }
        return {
            providerCompanyCode: providerCompanyCode,
            tpTaxReportFor: selectedCLIOption?.value as string,
            period: selectedPeriodNameOption?.value as string,
            accountingDay: selectedAccountingDayOption?.value as string,
        };
    }

    // Gated behind the reportCurrencyOverlay AppConfig flag. When enabled, the overlay shows the selected
    // CLI's FRAMES currency, read from the report's DETAIL "FRAMES Currency" row via buildCliCurrencyMap
    // (values are already bare codes, e.g. "USD"). This is only available once reportData has loaded; until
    // then (or when disabled) it resolves to '' so SectionWithCurrency renders no overlay. Provided to
    // children via context (below).
    const currencyValue: string = isReportCurrencyOverlayEnabled
        ? (buildCliCurrencyMap(reportData)[selectedCLIOption?.value as string] ?? '')
        : '';

    const filterGridDefinition = [
        ...(!dashboardDriven
            ? [{ colspan: isTpTaxCliEnrichmentEnabled ? 1 : 2 }, { colspan: 2 }]
            : []),
        { colspan: 2 }, // Company Code
        ...(isTpTaxCliEnrichmentEnabled ? [{ colspan: 2 }] : []), // Functional Segment
        { colspan: 3 }, // CLI (widest, to fit the CLI number + status badge)
        { colspan: 2 }, // Action buttons (View / Generate Report)
    ];

    return (
        <TPTaxReportProvider services={services} state={state} dispatch={dispatch} currency={currencyValue}>
            <div>
                <SpaceBetween size="l" direction="vertical">
                    <Container
                        header={
                            <Header
                                actions={
                                    <SpaceBetween direction="horizontal" size="xs">
                                        <Button variant="link" onClick={onResetButtonClicked}>
                                            Reset Filters
                                        </Button>
                                    </SpaceBetween>
                                }>
                                Report Filtering Criteria
                            </Header>
                        }>
                        <div>
                            <SpaceBetween direction="vertical" size="s">
                                {/* Company Code Type Toggle */}
                                {IS_TESTED_PARTY_REPORT_ENABLED && (
                                    <FormField label="Company Code Type">
                                        <SegmentedControl
                                            selectedId={companyCodeType}
                                            onChange={({ detail }) =>
                                                setCompanyCodeType(detail.selectedId as CompanyCodeType)
                                            }
                                            options={[
                                                { id: CompanyCodeType.PROVIDER, text: 'Provider Company Code' },
                                                { id: CompanyCodeType.TESTED_PARTY, text: 'Tested Party Company Code' },
                                            ]}
                                        />
                                    </FormField>
                                )}
                                <Grid gridDefinition={filterGridDefinition}>
                                    {/* Accounting Period + Accounting Day lead in legacy mode
                                        and are NOT gated on a company-code selection, so they can be chosen
                                        before the provider. When the event-driven redesign is on they come
                                        from the URL query and both dropdowns are hidden. */}
                                    {!dashboardDriven && (
                                        <SpaceBetween direction="vertical" size="xxs">
                                            <FormField label={isTpTaxCliEnrichmentEnabled ? 'Period' : 'Accounting Period'}>
                                                <Select
                                                    statusType={accountingPeriodLoading ? 'loading' : 'finished'}
                                                    loadingText="Fetching available options"
                                                    selectedOption={selectedPeriodNameOption}
                                                    onChange={({ detail }) =>
                                                        setSelectedPeriodNameOption(detail.selectedOption)
                                                    }
                                                    options={
                                                        accountingPeriodResult == null
                                                            ? []
                                                            : accountingPeriodResult.ReportingPeriodList.map(
                                                                  (accountingPeriodNameValue) => ({
                                                                      label: accountingPeriodNameValue,
                                                                      value: accountingPeriodNameValue,
                                                                  })
                                                              )
                                                    }
                                                    placeholder="Select a period"
                                                    virtualScroll={true}
                                                    filteringType="auto"
                                                />
                                            </FormField>
                                        </SpaceBetween>
                                    )}
                                    {!dashboardDriven && (
                                        <SpaceBetween direction="vertical" size="xxs">
                                            <FormField label="Accounting Day">
                                                <Select
                                                    selectedOption={selectedAccountingDayOption}
                                                    options={accountingDaysList}
                                                    onChange={({ detail }) => {
                                                        setSelectedAccountingDayOption(detail.selectedOption);
                                                    }}
                                                    selectedAriaLabel="Selected"
                                                    placeholder="Select Accounting Day"
                                                />
                                            </FormField>
                                        </SpaceBetween>
                                    )}
                                    <SpaceBetween direction="vertical" size="xxs">
                                        <FormField
                                            label={
                                                companyCodeType === CompanyCodeType.PROVIDER
                                                    ? 'Provider Company Code'
                                                    : 'Tested Party Company Code'
                                            }>
                                            <Select
                                                statusType={isCompanyCodesBySegmentDataLoading ? 'loading' : 'finished'}
                                                loadingText="Fetching available company codes"
                                                selectedOption={companyCodeOption}
                                                onChange={({ detail }) => {
                                                    resetOnCompanyCodeChange();
                                                    setCompanyCodeOption(detail.selectedOption);
                                                }}
                                                options={companyCodeBySegmentList}
                                                selectedAriaLabel="Selected"
                                                placeholder="Select the Company Code for report"
                                                virtualScroll={true}
                                                filteringType="auto"
                                            />
                                        </FormField>
                                    </SpaceBetween>

                                    {isTpTaxCliEnrichmentEnabled && (
                                        <SpaceBetween direction="vertical" size="xxs">
                                            <FormField label="Functional Segment">
                                                <Select
                                                    statusType={
                                                        getCLIsLoading || testedPartyMappingsLoading
                                                            ? 'loading'
                                                            : 'finished'
                                                    }
                                                    // Not selectable until a company code is selected.
                                                    disabled={dropDownDisabledFlag || !isOptionDefined(companyCodeOption)}
                                                    loadingText="Fetching functional segments"
                                                    selectedOption={selectedFunctionalSegmentOption}
                                                    onChange={({ detail }) => {
                                                        setSelectedFunctionalSegmentOption(detail.selectedOption);
                                                    }}
                                                    options={functionalSegmentOptions}
                                                    selectedAriaLabel="Selected"
                                                    placeholder="Select the Functional Segment"
                                                    virtualScroll={true}
                                                    filteringType="auto"
                                                />
                                            </FormField>
                                        </SpaceBetween>
                                    )}

                                    <SpaceBetween direction="vertical" size="xxs">
                                        <FormField label="CLI">
                                            <Select
                                                // Keep the CLI select in 'loading' while the period's
                                                // status set is in flight, so the user can't pick from the
                                                // list about to be narrowed to the report-scoped set.
                                                statusType={
                                                    getCLIsLoading ||
                                                    testedPartyMappingsLoading ||
                                                    reportStatusLoading
                                                        ? 'loading'
                                                        : 'finished'
                                                }
                                                disabled={
                                                    dropDownDisabledFlag ||
                                                    !isOptionDefined(companyCodeOption) ||
                                                    (companyCodeType === CompanyCodeType.TESTED_PARTY &&
                                                        !!testedPartyError) ||
                                                    // Report-status fetch failed: block selection so the
                                                    // user can't pick a CLI with no report for the period.
                                                    (isTpTaxCliEnrichmentEnabled && !!reportStatusError)
                                                }
                                                loadingText="Fetching available options"
                                                selectedOption={selectedCLIOption}
                                                onChange={({ detail }) => {
                                                    setSelectedCLIOption(detail.selectedOption);
                                                }}
                                                options={filteredCliList}
                                                selectedAriaLabel="Selected"
                                                placeholder="Select the CLI for report"
                                                virtualScroll={true}
                                                filteringType="auto"
                                            />
                                        </FormField>
                                    </SpaceBetween>
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'end',
                                            height: '100%',
                                        }}>
                                        <SpaceBetween size={'s'} direction={'horizontal'}>
                                            <Button
                                                variant="primary"
                                                onClick={onViewReportClicked}
                                                disabled={
                                                    !isOptionDefined(companyCodeOption) ||
                                                    !isOptionDefined(selectedPeriodNameOption) ||
                                                    !isOptionDefined(selectedCLIOption) ||
                                                    !isOptionDefined(selectedAccountingDayOption) ||
                                                    (companyCodeType === CompanyCodeType.TESTED_PARTY &&
                                                        !getProviderCodeForCli(
                                                            testedPartyMappings,
                                                            selectedCLIOption?.value as string
                                                        ))
                                                }
                                                loading={getPreviousReportLoading}>
                                                View Report
                                            </Button>
                                            {/* Generate Report is not offered in the dashboard-driven
                                                flow — report generation is initiated from the dashboard
                                                (Full Refresh) — so the button is hidden when the
                                                event-driven redesign is on. Legacy mode keeps it. */}
                                            {!dashboardDriven && (
                                                <Button
                                                    variant="normal"
                                                    disabled={
                                                        !isOptionDefined(companyCodeOption) ||
                                                        !isOptionDefined(selectedPeriodNameOption) ||
                                                        !isOptionDefined(selectedCLIOption) ||
                                                        !isOptionDefined(selectedAccountingDayOption) ||
                                                        !previousReportResponse ||
                                                        previousReportResponse?.reportStatus === 'running' ||
                                                        (companyCodeType === CompanyCodeType.TESTED_PARTY &&
                                                            !getProviderCodeForCli(
                                                                testedPartyMappings,
                                                                selectedCLIOption?.value as string
                                                            ))
                                                    }
                                                    onClick={onGenerateReportButtonClicked}
                                                    loading={generateReportLoading}>
                                                    Generate Report
                                                </Button>
                                            )}
                                        </SpaceBetween>
                                    </div>
                                </Grid>
                            </SpaceBetween>
                        </div>
                    </Container>
                    <AIInsightButton
                        cliId={selectedCLIOption?.value || ''}
                        reportType={AI_INSIGHT_REPORT_TYPES.TP_TAX}
                        accountingPeriod={selectedPeriodNameOption?.value || ''}
                        accountingDay={selectedAccountingDayOption?.value || ''}
                        defaultPrompt={buildTpTaxDefaultPrompt(
                            selectedCLIOption?.value || '',
                            selectedPeriodNameOption?.value || '',
                            selectedAccountingDayOption?.value || ''
                        )}
                        inline
                    />
                    {reportData && (
                        <>
                            <Tiles
                                onChange={({ detail }) => setReportCategory(detail.value)}
                                value={reportCategory}
                                items={[
                                    {
                                        label: CONSTANTS.TP_TAX_REPORT_CATEGORY.MARGIN,
                                        value: CONSTANTS.TP_TAX_REPORT_CATEGORY.MARGIN,
                                    },
                                    {
                                        label: CONSTANTS.TP_TAX_REPORT_CATEGORY.ALLOCATION,
                                        value: CONSTANTS.TP_TAX_REPORT_CATEGORY.ALLOCATION,
                                    },
                                ]}
                            />

                            <Container
                                header={
                                    <Header
                                        actions={
                                            <Button
                                                disabled={!previousReportResponse?.reportId || getPreviousReportLoading}
                                                onClick={() => {
                                                    setDownloadReportRequestPayload({
                                                        userId: useLocalStorage.getUsernameFromLocalStorage(),
                                                        reportId: previousReportResponse.reportId,
                                                    });
                                                }}
                                                loading={downloadReportLoading}>
                                                Download Full Report
                                            </Button>
                                        }>
                                        {reportCategory + ' Report'}
                                    </Header>
                                }>
                                {reportCategory == CONSTANTS.TP_TAX_REPORT_CATEGORY.MARGIN && (
                                    <>
                                        <SectionWithCurrency>
                                            <ExpandableSection variant={'container'} headerText="Details of CLI">
                                                <TPTaxReportTableGrid
                                                    data={reportData?.reportContent?.DETAIL}
                                                    services={services}
                                                    columnHeaders={reportData?.reportHeaders}
                                                    loadingStatus={getPreviousReportLoading}
                                                />
                                            </ExpandableSection>
                                        </SectionWithCurrency>
                                        <SectionWithCurrency>
                                            <ExpandableSection
                                                variant={'container'}
                                                headerText={
                                                    IS_TESTED_PARTY_REPORT_ENABLED
                                                        ? 'Tested Party TP P&L'
                                                        : 'TP Profit & Loss (P&L)'
                                                }
                                                headerActions={
                                                    <Button
                                                        onClick={() =>
                                                            history.push(
                                                                `${MANAGE_EXCEPTIONS_TAB_URL_PREFIX}?cli=${selectedCLIOption?.value as string}`
                                                            )
                                                        }>
                                                        Manage P&L Mapping
                                                    </Button>
                                                }>
                                                <TPTaxReportTableGrid
                                                    data={reportData?.reportContent?.TP_PNL}
                                                    services={services}
                                                    columnHeaders={reportData?.reportHeaders}
                                                    loadingStatus={getPreviousReportLoading}
                                                />
                                            </ExpandableSection>
                                        </SectionWithCurrency>
                                        <SectionWithCurrency>
                                            <ExpandableSection
                                                variant={'container'}
                                                headerText={
                                                    IS_TESTED_PARTY_REPORT_ENABLED
                                                        ? 'Tested Party PLI'
                                                        : 'Profit Level Indicator (PLI)'
                                                }
                                                headerActions={
                                                    <Button
                                                        onClick={() =>
                                                            window.open(
                                                                CONSTANTS.PAGE_NAV.CALCULATION_BUILDER.URL +
                                                                    `/${btoa(cliDetails.cli.recordId as string)}`
                                                            )
                                                        }
                                                        loading={cliDetailsLoading}
                                                        disabled={cliDetailsError !== null || !cliDetails?.cli?.recordId}
                                                        external={true}>
                                                        Manage PLI Configuration
                                                    </Button>
                                                }>
                                                <Tabs
                                                    tabs={Object.entries(reportData?.reportContent || {})
                                                        .filter(([key]) => isPliTabKey(key))
                                                        .map(([key, value]) => ({
                                                            label: key,
                                                            id: key,
                                                            content: (
                                                                <TPTaxReportTableGrid
                                                                    data={value}
                                                                    services={services}
                                                                    columnHeaders={reportData?.reportHeaders}
                                                                    loadingStatus={getPreviousReportLoading}
                                                                />
                                                            ),
                                                        }))}
                                                />
                                            </ExpandableSection>
                                        </SectionWithCurrency>
                                        {isPassthroughDrilldownEnabled && (
                                            <>
                                                <PassthroughCostDetailSection
                                                    passthroughCostDetail={reportData?.passthroughCostDetail}
                                                    serviceCollection={services}
                                                />
                                                <PLIReconciliationSection
                                                    pliReconciliation={reportData?.pliReconciliation}
                                                    serviceCollection={services}
                                                />
                                            </>
                                        )}
                                    </>
                                )}
                                {reportCategory == CONSTANTS.TP_TAX_REPORT_CATEGORY.ALLOCATION && (
                                    <>
                                        <SectionWithCurrency>
                                            <ExpandableSection variant={'container'} headerText="Details of CLI">
                                                <TPTaxReportTableGrid
                                                    data={reportData?.reportContent?.DETAIL}
                                                    services={services}
                                                    columnHeaders={reportData?.reportHeaders}
                                                    loadingStatus={getPreviousReportLoading}
                                                />
                                            </ExpandableSection>
                                        </SectionWithCurrency>
                                        <SectionWithCurrency>
                                            <ExpandableSection variant={'container'} headerText="Allocation">
                                                <TPTaxReportTableGrid
                                                    data={reportData?.reportContent?.ALLOCATION}
                                                    services={services}
                                                    columnHeaders={reportData?.reportHeaders}
                                                    loadingStatus={getPreviousReportLoading}
                                                />
                                            </ExpandableSection>
                                        </SectionWithCurrency>
                                    </>
                                )}
                            </Container>
                        </>
                    )}
                </SpaceBetween>
                <TPEErrorWatcher
                    services={services}
                    errors={[
                        getCLIsError,
                        companyCodesBySegmentFetchError,
                        getPreviousReportError,
                        accountingPeriodError,
                        cliDetailsError,
                        generateReportError,
                        testedPartyError,
                        // Surface a failed report-status fetch rather than silently showing the full list.
                        reportStatusError,
                    ]}
                />
            </div>
        </TPTaxReportProvider>
    );
}
